import React, { useCallback, useState } from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Item } from '../../models/Item'
import { RecordFactory } from '../../models/RecordFactory'
import { reorderArray, sortBySeqnum } from '../../utils'
import { getIconNameFromType, plural } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { Icon } from '../Icon'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { ArrangeableTable } from '../common/ArrangeableTable'
import { SettingsLayout } from './SettingsLayout'

const viewWillAppear = async (base: PocketComponent<void>) => {
    const items = await RecordFactory.getInstance().loadItems(base.owner.id!)

    sortBySeqnum(items)

    return { items }
}

function ItemIcon({ item }: { item: Item }) {
    if (item.image?.url) {
        return <img src={item.image.url} alt={item.name} className="size-9 rounded-full" />
    }
    return (
        <Icon
            icon={`${getIconNameFromType(item.type ?? '')}.medium`}
            shouldInheritColor
            background="round"
            className="text-primary-700 size-9"
        />
    )
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const { logAndDisplayError } = useErrorHandler()

    const [updating, setUpdating] = useState(false)
    const [sortedItems, setSortedItems] = useState(data.items)

    const onDragEnd = async (source: Item, position: number, list: Item[]) => {
        setUpdating(true)
        const originalItemList = sortedItems
        setSortedItems(
            list.map((c, seqnum) => {
                c.seqnum = seqnum + 1
                return c
            })
        )

        const result = await RecordFactory.getInstance().moveItem(source, position)
        setUpdating(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
            setSortedItems(originalItemList)
            return
        }
    }

    return (
        <SettingsLayout title="Sort services and products">
            <ArrangeableTable
                headers={[
                    {
                        label: 'Name',
                    },
                    {
                        label: 'Type',
                    },
                    {
                        label: 'Price',
                        className: 'text-right',
                    },
                ]}
                droppableId="categories"
                onDragEnd={(s, d, l) => {
                    onDragEnd(
                        s.category,
                        d,
                        l.map((r) => r.category)
                    )
                }}
                rows={sortedItems.map((item) => ({
                    id: item.id ?? '',
                    title: item.name ?? '',
                    category: item,
                    href: `/settings/${plural(item.type ?? '')}/${item.id}/edit`,
                    cells: [
                        <div className="flex items-center gap-x-4">
                            <ItemIcon item={item} />
                            <Label size="sm">{item.name}</Label>
                        </div>,
                        <Paragraph size="sm" className="capitalize">
                            {item.type ?? ''}
                        </Paragraph>,
                        <Paragraph size="sm" className="text-right">
                            {currencyFormat(item.rate, true)}
                        </Paragraph>,
                    ],
                }))}
            />
        </SettingsLayout>
    )
}

export const SetupItemsSortPage = usePSComponent(render, viewWillAppear)
